class FloatingPromoBanner extends HTMLElement {
  constructor() {
    super();

    this.storageKey = this.dataset.storageKey;
    this.closePersistence = this.dataset.closePersistence || 'session';
    this.card = this.querySelector('.floating-promo-banner__card');
    this.closeButton = this.querySelector('[data-floating-promo-close]');
    this.openButton = this.querySelector('[data-floating-promo-open]');
    this.modal = this.querySelector('[data-floating-promo-modal]');
    this.modalPanel = this.querySelector('[data-floating-promo-modal-panel]');
    this.modalClosers = this.querySelectorAll('[data-floating-promo-modal-close]');
    this.newsletterForm = this.querySelector('[data-newsletter-form]');
    this.lastFocusedElement = null;
    this.modalCloseTimer = null;

    this.handleKeydown = this.handleKeydown.bind(this);
    this.openModal = this.openModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.closeCard = this.closeCard.bind(this);
  }

  connectedCallback() {
    if (this.shouldHideCard()) {
      this.classList.add('is-card-hidden');
    }

    this.closeButton?.addEventListener('click', this.closeCard);
    this.openButton?.addEventListener('click', this.openModal);
    this.modalClosers.forEach((button) => button.addEventListener('click', this.closeModal));
    document.addEventListener('keydown', this.handleKeydown);

    window.setTimeout(() => {
      if (this.newsletterForm?.classList.contains('has-success') || this.newsletterForm?.classList.contains('has-error')) {
        this.classList.remove('is-hidden');
        this.openModal();
      }
    }, 60);
  }

  disconnectedCallback() {
    this.closeButton?.removeEventListener('click', this.closeCard);
    this.openButton?.removeEventListener('click', this.openModal);
    this.modalClosers.forEach((button) => button.removeEventListener('click', this.closeModal));
    document.removeEventListener('keydown', this.handleKeydown);
    window.clearTimeout(this.modalCloseTimer);
  }

  shouldHideCard() {
    if (this.closePersistence !== 'session' || !this.storageKey) return false;

    try {
      return window.sessionStorage?.getItem(this.storageKey) === 'closed';
    } catch (error) {
      return false;
    }
  }

  closeCard() {
    if (this.classList.contains('is-card-hidden') || this.classList.contains('is-card-closing')) return;

    if (this.closePersistence === 'session' && this.storageKey) {
      try {
        window.sessionStorage?.setItem(this.storageKey, 'closed');
      } catch (error) {
        // Ignore storage errors so the close button always works.
      }
    }

    if (!this.card || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      this.classList.add('is-card-hidden');
      return;
    }

    this.classList.add('is-card-closing');

    let hasFinished = false;
    const finishClosing = () => {
      if (hasFinished) return;
      hasFinished = true;
      window.clearTimeout(fallbackTimer);
      this.classList.remove('is-card-closing');
      this.classList.add('is-card-hidden');
    };
    const fallbackTimer = window.setTimeout(finishClosing, 460);

    this.card.addEventListener('animationend', finishClosing, { once: true });
  }

  openModal() {
    if (!this.modal) return;

    window.clearTimeout(this.modalCloseTimer);
    this.lastFocusedElement = document.activeElement;
    this.modal.hidden = false;
    this.classList.add('is-modal-open');

    requestAnimationFrame(() => {
      this.modal.classList.add('is-visible');
      this.lockScroll();

      const focusTarget = this.modal.querySelector('input:not([type="hidden"]), textarea, select, button, a');
      focusTarget?.focus({ preventScroll: true });
    });
  }

  closeModal(event) {
    event?.preventDefault();
    if (!this.modal || this.modal.hidden || !this.modal.classList.contains('is-visible')) return;

    this.modal.classList.remove('is-visible');
    this.unlockScroll();

    const finishClosing = () => {
      this.modal.hidden = true;
      this.classList.remove('is-modal-open');
      this.modalCloseTimer = null;
    };

    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      finishClosing();
    } else {
      this.modalCloseTimer = window.setTimeout(finishClosing, 260);
    }

    if (this.lastFocusedElement && typeof this.lastFocusedElement.focus === 'function') {
      this.lastFocusedElement.focus({ preventScroll: true });
    }
  }

  lockScroll() {
    if (window.theme?.a11y?.trapFocus && this.modalPanel) {
      window.theme.a11y.trapFocus({ container: this.modalPanel });
    }

    document.dispatchEvent(new CustomEvent('theme:scroll:lock', {
      bubbles: true,
      detail: this.modalPanel || this.modal,
    }));
  }

  unlockScroll() {
    if (window.theme?.a11y?.removeTrapFocus) {
      window.theme.a11y.removeTrapFocus();
    }

    document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
  }

  handleKeydown(event) {
    if (event.code === window.theme?.keyboardKeys?.ESCAPE || event.key === 'Escape') {
      this.closeModal(event);
    }
  }
}

if (!customElements.get('floating-promo-banner')) {
  customElements.define('floating-promo-banner', FloatingPromoBanner);
}
